import * as yup from 'yup';

import { Utils } from '@helpers/utils';
import { BranchModel } from '@models/branch.model';
import { ClientModel } from '@models/client.model';
import { PurchaseOrderModel } from '@models/purchaseOrder.model';
import { RegionModel } from '@models/region.model';
import { JobTypeModel, RequisitionModel } from '@models/requisition.model';
import { SiteModel } from '@models/site.model';
export type MasterDataType = 'branch' | 'site' | 'requisition' | 'purchaseOrder' | 'client' | 'jobType';

export type FieldMapper = Record<
  string,
  {
    key: string;
    dependOn?: { entity: string; key: string; fallbackMatch?: string[]; getFromFile?: boolean };
    default?: any;
    transform?: (val: any) => any;
  }
>;
export const branchFieldMapper: FieldMapper = {
  id: {
    key: 'id',
    default: null,
    dependOn: { entity: 'branches', key: 'id', fallbackMatch: ['siteId', 'name', 'sid'], getFromFile: true },
  },
  siteId: { key: 'siteId', default: null, dependOn: { entity: 'sites', key: 'id' } },
  name: { key: 'name', dependOn: { entity: 'branches', key: 'name' } },
  isActive: { key: 'isActive', transform: (val) => String(val).toLowerCase() === 'true' },
  departmentId: { key: 'departmentId', default: null },
  sid: { key: 'sid' },
  nl: { key: 'nl' },
  street: { key: 'street' },
  streetNumber: { key: 'streetNumber' },
  city: { key: 'city' },
  postalCode: { key: 'postalCode' },
  phone: { key: 'phone' },
  fax: { key: 'fax' },
  email: { key: 'email' },
  web: { key: 'web' },
  regionId: { key: 'regionId', default: null, dependOn: { entity: 'regions', key: 'id' } },
  region: { key: 'region', default: null, dependOn: { entity: 'regions', key: 'name' } },
  isValid: { key: 'isValid', transform: (val) => String(val).toLowerCase() === 'true' },
};

export const siteFieldMapper: FieldMapper = {
  id: {
    key: 'id',
    default: null,
    dependOn: { entity: 'sites', key: 'id', fallbackMatch: ['code'], getFromFile: true },
  },
  company: { key: 'company' },
  name: { key: 'name' },
  isActive: { key: 'isActive', transform: (val) => String(val).toLowerCase() === 'true' },
  branchName: { key: 'branchName' },
  prefix: { key: 'prefix' },
  code: { key: 'code' },
  externalLink: { key: 'externalLink' },
  isValid: { key: 'isValid', transform: (val) => String(val).toLowerCase() === 'true' },
};

export const requisitionFieldMapper: FieldMapper = {
  requisitionId: {
    key: 'requisitionId',
    default: null,
    dependOn: { entity: 'requisitions', key: 'requisitionId', getFromFile: true },
  },
  title: { key: 'title' },
  siteId: { key: 'siteId', default: null, dependOn: { entity: 'sites', key: 'id' } },
  jobTypeId: { key: 'jobTypeId', default: null, dependOn: { entity: 'jobTypes', key: 'id' } },
  isValid: { key: 'isValid', transform: (val) => String(val).toLowerCase() === 'true' },
};
export const purchaseOrderFieldMapper: FieldMapper = {
  requisitionId: {
    key: 'poNumber',
    default: null,
    dependOn: { entity: 'purchaseOrders', key: 'poNumber', getFromFile: true },
  },
  id: {
    key: 'id',
    default: null,
    dependOn: { entity: 'purchaseOrders', key: 'id', fallbackMatch: ['code'], getFromFile: true },
  },
  siteId: { key: 'clientId', default: null, dependOn: { entity: 'clients', key: 'id' } },
  isValid: { key: 'isValid', transform: (val) => String(val).toLowerCase() === 'true' },
};
export const clientFieldMapper: FieldMapper = {
  id: {
    key: 'id',
    default: null,
    dependOn: { entity: 'clients', key: 'id', getFromFile: true },
  },
  name: { key: 'name' },
  customerId: { key: 'customerId' },
  isValid: { key: 'isValid', transform: (val) => String(val).toLowerCase() === 'true' },
  siteId: { key: 'siteId', default: null, dependOn: { entity: 'sites', key: 'id' } },
};
export const jobTypeFieldMapper: FieldMapper = {
  id: {
    key: 'id',
    default: null,
    dependOn: { entity: 'jobTypes', key: 'id', getFromFile: true },
  },
  name: { key: 'name' },
  isValid: { key: 'isValid', transform: (val) => String(val).toLowerCase() === 'true' },
};

function findEntity<T extends Record<string, any>>(
  list: T[],
  matchKeys: string[] | string,
  matchValues: any | Record<keyof T, any>,
): T | undefined {
  if (Array.isArray(matchKeys)) {
    return list.find((item) =>
      matchKeys.every((key) => {
        return item[key] === matchValues[key.toLowerCase()];
      }),
    );
  } else {
    return list.find((item) => item[matchKeys] === matchValues);
  }
}

export const toSafeRow = <T>(
  normalizedRow: Record<string, any>,
  fieldMap: FieldMapper,
  dependencies?: {
    regions?: RegionModel[];
    clients: ClientModel[];
    sites?: SiteModel[];
    branches?: BranchModel[];
    purchaseOrders?: PurchaseOrderModel[];
    requisitions?: RequisitionModel[];
    jobTypes?: JobTypeModel[];
    [key: string]:
      | RegionModel[]
      | SiteModel[]
      | BranchModel[]
      | RequisitionModel[]
      | JobTypeModel[]
      | PurchaseOrderModel[]
      | ClientModel[]
      | undefined;
  },
): { result: T; error: { field: string; message: string }[] } => {
  const result: any = {};
  const error: { field: string; message: string }[] = [];
  for (const [field, { key, transform }] of Object.entries(fieldMap)) {
    const value = normalizedRow[key.toLowerCase()];
    result[field] = transform ? transform(value) : value ?? '';
    if (result[field] === undefined || result[field] === null || result[field] === '') {
      fieldMap[field].default === null && delete result[field];
    }
    const dependOn = fieldMap[field]?.dependOn;
    if (dependencies && dependOn?.entity && dependencies[dependOn.entity]) {
      const entity = dependencies[dependOn.entity];
      const hasKey = result?.[fieldMap[field].key];
      const dependancyKey = fieldMap[field].dependOn?.key;
      if (hasKey) {
        if (Array.isArray(entity) && entity.length > 0 && dependancyKey) {
          const valueFromEntity = findEntity(entity as any[], dependancyKey, value)?.[dependancyKey];
          if (!valueFromEntity) {
            if (dependOn.getFromFile) {
              result[field] = value; // if getFromFile is true, we will use the value from the file even if it doesn't match any existing entity. This allows for creating new entities based on the import file.
              continue;
            }
            error.push({
              field,
              message: `No matching ${dependOn.entity.slice(0, -1)} found for ${field} with value "${result[field]}"`,
            });
            continue;
          }
          result[field] = valueFromEntity;
        }
      } else if (dependOn.fallbackMatch) {
        const properties = dependOn.fallbackMatch;
        const valueFromEntity = findEntity(entity as any[], properties, normalizedRow)?.[field];
        if (valueFromEntity) result[field] = valueFromEntity;
      }
    }
  }
  return { result, error };
};

type ValidationErrorItem = {
  property: string;
  constraints: { [key: string]: string };
};

export function parseNestValidationErrors(validationErrors: any): Record<string, string> {
  const output: Record<string, string> = {};

  const errors: ValidationErrorItem[] = validationErrors || [];

  for (const err of errors) {
    const field = err.property;
    const messages = Object.values(err.constraints || {});
    if (messages.length > 0) {
      output[field] = messages[0]; // take the first constraint message
    }
  }

  return output;
}
export const uuidOptional = () =>
  yup
    .string()
    .nullable()
    .notRequired()
    .test('is-uuid-or-empty', 'Must be a valid UUID or empty', (value) => {
      if (!value) return true; // allow empty, null, or undefined
      return Utils.uuidValidator(value);
    });

export const validationSchema = {
  branch: yup.object().shape({
    name: yup.string().required('Branch Name is required'),
    siteId: uuidOptional(),
    externalBranchId: uuidOptional(),
    isActive: yup.boolean().nullable(),
    departmentId: yup.string().nullable().default(''),
    sid: yup.string().required('SID is required'),
    nl: yup.string().required('Native Label is required'),
    street: yup.string().required('Street is required'),
    streetNumber: yup.string().required('Street Number is required'),
    city: yup.string().required('City is required'),
    postalCode: yup.string().required('Postal Code is required'),
    phone: yup.string().required('Phone is required'),
    fax: yup.string().nullable(),
    email: yup.string().required('Email is required').email('Invalid email'),
    web: yup.string().nullable(),
    regionId: yup.string().nullable(),
    isValid: yup.boolean().nullable(),
    roleCode: yup.string().nullable(),
    customData: yup.string().optional(),
  }),
  site: yup.object().shape({
    company: yup.string().nullable(),
    prefix: yup.string().required('Prefix is required'),
    branchName: yup.string().nullable(),
    name: yup.string().required('Name is required'),
    code: yup.string().required('Code is required'),
    externalLink: yup.string().nullable(),
    isValid: yup.boolean().nullable(),
  }),
  requisition: yup.object().shape({
    requisitionId: yup.string().required('Requisition ID is required'),
    jobTypeId: yup.string().required('Job Type ID is required'),
    siteId: yup.string().required('Site ID is required'),
    isValid: yup.boolean().required('IsValid is required'),
    title: yup.string().nullable(),
    link: yup.string().nullable(),
  }),
  purchaseOrder: yup.object().shape({
    poNumber: yup.string().required('purcahse order number is required'),
    clientId: yup
      .string()
      .required('Client Id is required')
      .test('is-uuid-or-empty', 'Must be a valid UUID or empty', (value) => {
        if (!value) return true; // allow empty, null, or undefined
        return Utils.uuidValidator(value);
      }),
    isValid: yup.boolean().required('IsValid is required'),
  }),
  client: yup.object().shape({
    name: yup.string().optional(),
    isValid: yup.boolean().required('IsValid is required'),
    siteId: yup
      .string()
      .required('Site Id is required')
      .test('is-uuid-or-empty', 'Must be a valid UUID or empty', (value) => {
        if (!value) return true; // allow empty, null, or undefined
        return Utils.uuidValidator(value);
      }),
    customerId: yup.string().required('Customer Id is required'),
  }),
  jobType: yup.object().shape({
    name: yup.string().required('Client Name is required'),
    isValid: yup.boolean().required('IsValid is required'),
  }),
};
export const initialValues = {
  branch: {
    siteId: null,
    name: '',
    isActive: false,
    departmentId: '',
    sid: '',
    nl: '',
    street: '',
    streetNumber: '',
    city: '',
    postalCode: '',
    phone: '',
    fax: '',
    email: '',
    web: '',
    regionId: null,
    isValid: false,
    externalBranchId: '',
    roleCode: '',
    customData: '"{}"',
  },
  site: {
    company: '',
    prefix: '',
    branchName: '',
    name: '',
    code: '',
    externalLink: '',
    isValid: false,
  },
  requisition: {
    requisitionId: '',
    jobTypeId: '',
    siteId: '',
    title: '',
    isValid: false,
    link: undefined,
  },
  purchaseOrder: {
    poNumber: '',
    clientId: '',
    isValid: false,
  },
  client: {
    customerId: '',
    name: '',
    siteId: '',
    isValid: false,
  },
  jobType: {
    name: '',
    isValid: false,
  },
};
export const formItems: {
  [key in MasterDataType]: { name: string; label: string; component: string; useName?: boolean }[];
} = {
  branch: [
    { name: 'departmentId', label: 'Department ID', component: 'TextField' },
    { name: 'externalBranchId', label: 'External BranchId', component: 'TextField' },
    { name: 'name', label: 'Branch Name', component: 'TextField' },
    { name: 'sid', label: 'SID', component: 'TextField' },
    { name: 'nl', label: 'Native Label', component: 'TextField' },
    { name: 'street', label: 'Street', component: 'TextField' },
    { name: 'streetNumber', label: 'Street Number', component: 'TextField' },
    { name: 'city', label: 'City', component: 'TextField' },
    { name: 'postalCode', label: 'Postal Code', component: 'TextField' },
    { name: 'phone', label: 'Phone', component: 'TextField' },
    { name: 'fax', label: 'Fax', component: 'TextField' },
    { name: 'email', label: 'Email', component: 'TextField' },
    { name: 'web', label: 'Website', component: 'TextField' },
    { name: 'roleCode', label: 'Role Code', component: 'TextField' },
    { name: 'customData', label: 'Custom Data', component: 'JsonEditor' },
    { name: 'regionId', label: 'Region', component: 'Select' },
    { name: 'siteId', label: 'Site', component: 'Select' },
    { name: 'isActive', label: 'Is Active', component: 'Checkbox' },
    { name: 'isValid', label: 'Is Valid', component: 'Checkbox' },
  ],
  site: [
    { name: 'name', label: 'Site Name', component: 'TextField' },
    { name: 'prefix', label: 'Prefix', component: 'TextField' },
    { name: 'code', label: 'Code', component: 'TextField' },
    { name: 'company', label: 'Company', component: 'TextField' },
    { name: 'externalLink', label: 'External Link', component: 'TextField' },
    { name: 'branchName', label: 'Branch Name', component: 'Select', useName: true },
    { name: 'isValid', label: 'Is Valid', component: 'Checkbox' },
  ],
  requisition: [
    { name: 'requisitionId', label: 'Requisition ID', component: 'TextField' },
    { name: 'title', label: 'Title', component: 'TextField' },
    { name: 'link', label: 'Link', component: 'TextField' },
    { name: 'jobTypeId', label: 'Job Type', component: 'Select' },
    { name: 'siteId', label: 'Site', component: 'Select' },
    { name: 'isValid', label: 'Is Valid', component: 'Checkbox' },
  ],
  purchaseOrder: [
    { name: 'poNumber', label: 'Purcahse Order Number', component: 'TextField' },
    { name: 'clientId', label: 'Client Id', component: 'Select' },
    { name: 'isValid', label: 'Is Valid', component: 'Checkbox' },
  ],
  client: [
    { name: 'customerId', label: 'Customer Id', component: 'TextField' },
    { name: 'name', label: 'Name', component: 'TextField' },
    { name: 'siteId', label: 'Site', component: 'Select' },
    { name: 'isValid', label: 'Is Valid', component: 'Checkbox' },
  ],
  jobType: [
    { name: 'name', label: 'Name', component: 'TextField' },
    { name: 'isValid', label: 'Is Valid', component: 'Checkbox' },
  ],
};
